import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Scale, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shared from '@/styles/public.module.css';
import shell from '@/styles/authShell.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmail = () => {
  usePageMeta(PAGE_META.verifyEmail);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');
  const [verifiedRole, setVerifiedRole] = useState('');
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link.');
      return;
    }
    if (hasAttemptedRef.current) return;
    hasAttemptedRef.current = true;

    const run = async () => {
      try {
        const response = await fetch(`${API}/auth/verify-email`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token })
        });
        if (response.ok) {
          const data = await response.json().catch(() => ({}));
          setVerifiedEmail(data.email || '');
          setVerifiedRole(data.role || '');
          setStatus('success');
          setMessage('Your email has been successfully verified!');
          toast.success('Email verified!');
        } else {
          const err = await response.json();
          setStatus('error');
          setMessage(err.detail || 'Verification failed.');
        }
      } catch {
        setStatus('error');
        setMessage('Verification failed. Please try again.');
      }
    };
    run();
  }, [searchParams]);

  const buildLoginHref = () => {
    const params = new URLSearchParams();
    params.set('verified', '1');
    if (verifiedEmail) params.set('email', verifiedEmail);
    if (verifiedRole) params.set('role', verifiedRole);
    return `/login?${params.toString()}`;
  };

  return (
    <div className={shell.page} data-testid="verify-email-page">
      <header className={shell.header}>
        <div className={shell.headerInner}>
          <button type="button" className={shell.logo} onClick={() => navigate('/')} aria-label="MyCaseLoan home">
            <Scale size={26} aria-hidden="true" />
            <span className={shell.logoText}>MyCaseLoan</span>
          </button>
        </div>
      </header>

      <main className={shell.centre}>
        <div className={shell.inner}>
          <div className={shell.card} data-testid="verify-email-card">
            <div className={shell.head}>
              {status === 'verifying' && (
                <>
                  <div className={shell.statusIcon}>
                    <Loader2 size={32} className={shell.spinner} aria-hidden="true" />
                  </div>
                  <h1 className={shell.title}>Verifying Email</h1>
                  <p className={shell.subtitle}>
                    Please wait while we verify your email address...
                  </p>
                </>
              )}

              {status === 'success' && (
                <>
                  <div className={shell.statusIcon}>
                    <CheckCircle size={36} aria-hidden="true" />
                  </div>
                  <h1 className={shell.title}>Email Verified!</h1>
                  <p className={shell.subtitle}>{message}</p>
                </>
              )}

              {status === 'error' && (
                <>
                  <div className={`${shell.statusIcon} ${shell.statusError}`}>
                    <XCircle size={36} aria-hidden="true" />
                  </div>
                  <h1 className={shell.title}>Verification Failed</h1>
                  <p className={shell.subtitle}>{message}</p>
                </>
              )}
            </div>

            {status === 'success' && (
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                onClick={() => navigate(buildLoginHref())}
                data-testid="go-to-login-button"
              >
                Go to Login
              </button>
            )}

            {status === 'error' && (
              <div className={shell.stack}>
                <button
                  type="button"
                  className={`${shared.btn} ${shared.btnAction} ${shell.fullBtn}`}
                  onClick={() => navigate('/login')}
                  data-testid="go-to-login-button"
                >
                  Go to Login
                </button>
                <button
                  type="button"
                  className={`${shared.btn} ${shared.btnQuiet} ${shell.fullBtn}`}
                  onClick={() => navigate('/signup')}
                  data-testid="create-account-button"
                >
                  Create New Account
                </button>
              </div>
            )}
          </div>

          <p className={shell.footNote}>
            &copy; {new Date().getFullYear()} MyCaseLoan. All rights reserved.
          </p>
        </div>
      </main>
    </div>
  );
};

export default VerifyEmail;
